import _, { cloneDeep } from "lodash";
import React, { Fragment, useEffect, useState } from "react";
import Select from "react-select";

import Button from "@/components/UI/Button";
import CardSecondary from "@/components/UI/Card/CardSecondary";
import images from "@/utils/images";
import InputError from "@/components/UI/InputError";
import { joinDiagnosis } from "@/utils/noteUtils";
import { testLocator } from "@/lib/helpers/test-locator";

import { BehavioralGoalsRatingLabelButtons } from "../../BehavioralGoalsRatingLabelButtons";
import { getDefaultMedicationObj } from "../helpers/getDefaultMedicationObj";
import { MED_CHANGES_OPTIONS } from "../../../constants/medication-changes";
import { MEDICATION_IMPACT_SECTIONS } from "../constants/medication-label-map";

export const PrescriberMedicationsV2 = (props) => {
  let { editable, errors, note, profileView } = props;
  const [medications, setMedications] = useState(_.cloneDeep(note.content.medications));

  let allDiagnosis = joinDiagnosis(note);
  let medicationContent = [];

  const handleMedicationChange = (e, index, key, labels) => {
    let clonedMedications = _.cloneDeep(medications);

    clonedMedications[index][key] = e.target.value;
    clonedMedications[index][key + "_labels"] = labels;
    setMedications(clonedMedications);
    props.editHandler("medications", clonedMedications);
  };

  const addMedication = () => {
    const clonedMedications = cloneDeep(medications);

    clonedMedications.push(getDefaultMedicationObj());
    setMedications(clonedMedications);
    props.editHandler("medications", clonedMedications);
  };

  const removeMedication = (index) => {
    const clonedMedications = cloneDeep(medications);

    clonedMedications.splice(index, 1);
    setMedications(clonedMedications);
    props.editHandler("medications", clonedMedications);
  };

  const buildMedicationImpactSection = (medication, index) => {
    return MEDICATION_IMPACT_SECTIONS.map((section) => {
      medication[section.key] =
        typeof medication[section.key] === "string"
          ? parseInt(medication[section.key])
          : medication[section.key];

      return (
        <Fragment key={section.key}>
          <span className="tw:font-bold">{section.title}</span>
          <BehavioralGoalsRatingLabelButtons
            isEditing={editable}
            onClick={(e) => handleMedicationChange(e, index, section.key, section.labels)}
            optionLabelMap={section.labels}
            progress={medication}
            ratingKey={section.key}
          />
        </Fragment>
      );
    });
  };

  const buildMedicationContent = () => {
    medicationContent = medications.map((medication, index) => {
      let isNew = medication.med_changes === "ADD";
      let title = isNew ? "New Medication" : "Medication(s)";

      let medChangeOptions;
      let medChangeDefault;

      if (isNew) {
        medChangeOptions = [{ label: "Add", value: "ADD" }];
        medChangeDefault = medChangeOptions[0];
      } else {
        medChangeOptions = MED_CHANGES_OPTIONS;
        medChangeDefault = medChangeOptions.find(
          (option) => option.value === medication.med_changes,
        );
      }

      return (
        <>
          <div className="clr-sec px-3 py-2 d-flex flex-column-reverse tw:font-bold">
            <span>{title}</span>
            {editable && isNew && (
              <img
                alt=""
                className="cursor align-self-end"
                onClick={() => {
                  removeMedication(index);
                }}
                src={images("./common/cross.svg")}
                width="8"
              />
            )}
          </div>

          <div className="border-bottom pb-3">
            <div className="row px-4">
              <div className="col-lg-4 col-12 col-sm-4">
                <span>Name</span>
                <input
                  className="w-100 form-control"
                  disabled={!isNew || !editable}
                  onChange={(e) => handleMedicationChange(e, index, "medication_name")}
                  value={medication.medication_name}
                  {...testLocator(`medication-name-${index}`)}
                />

                {isNew && errors["medication_name"] ? (
                  <InputError classes="custom-error">{errors["medication_name"]}</InputError>
                ) : (
                  ""
                )}
              </div>

              <div className="col-lg-4 col-12 col-sm-4">
                <span>Dosage</span>
                <input
                  className="w-100 form-control"
                  disabled={!editable}
                  onChange={(e) => handleMedicationChange(e, index, "dosage")}
                  value={medication.dosage}
                  {...testLocator(`medication-dosage-${index}`)}
                />
                {isNew && errors["dosage"] ? (
                  <InputError classes="custom-error">{errors["dosage"]}</InputError>
                ) : (
                  ""
                )}
              </div>

              <div className="col-lg-4 col-12 col-sm-4">
                {!isNew && buildMedicationImpactSection(medication, index)}
              </div>
            </div>

            <div className="row px-4">
              <div className="col-lg-4 col-12 col-sm-4">
                <span>Medication Changes</span>
                <Select
                  className="basic-multi-select w-100"
                  classNamePrefix="select"
                  defaultValue={medChangeDefault}
                  isDisabled={isNew || !editable}
                  onChange={(selectedOption) => {
                    handleMedicationChange(
                      { target: { value: selectedOption.value } },
                      index,
                      "med_changes",
                    );
                  }}
                  options={medChangeOptions}
                />
              </div>
              <div className="col-lg-4 col-12 col-sm-4">
                <span>Diagnosis</span>
                <Select
                  className="basic-multi-select w-100"
                  classNamePrefix="select"
                  defaultValue={medication.diagnosis}
                  isDisabled={!editable}
                  isMulti
                  onChange={(selectedOptions) => {
                    handleMedicationChange(
                      { target: { value: selectedOptions } },
                      index,
                      "diagnosis",
                    );
                  }}
                  options={allDiagnosis}
                />
              </div>
            </div>
          </div>
        </>
      );
    });
  };

  useEffect(() => {
    note = props.note;
    setMedications(note.content.medications);
  }, [props.note.content]);

  buildMedicationContent();

  let buttonDisplay = editable ? "" : "d-none";
  let innerContent = (
    <>
      {medicationContent}
      <div className="d-flex tw:justify-center">
        <Button
          className={`Btn Btn--pri Btn-sm mt-3 mb-3 ${buttonDisplay}`}
          onClick={addMedication}
          {...testLocator("addMedicationBtn")}
        >
          Add Medication
        </Button>
      </div>
    </>
  );

  let mobileContent = (
    <CardSecondary className="mx-auto d-block d-md-block d-lg-none mt-3">
      {innerContent}
    </CardSecondary>
  );

  let desktopContent = (
    <CardSecondary className="mx-auto px-3 py-3 d-none d-md-none d-lg-block">
      {innerContent}
    </CardSecondary>
  );

  if (profileView) {
    desktopContent = (
      <div className="mx-auto px-3 py-3 d-none d-md-none d-lg-block">{innerContent}</div>
    );
  }

  return (
    <>
      {desktopContent}
      {mobileContent}
    </>
  );
};
